import { FiX, FiClock } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useTabStore, getOldestClosableTab, MAX_OPEN_TABS } from '../../store/tabStore'
import { useLanguageStore } from '../../store/languageStore'
import { useTranslation } from '../../hooks/useTranslation'
import { clearPersistentStatesForPath } from '../../hooks/usePersistentFormState'
import { destroyKeepAliveForTab } from './keepAliveBridge'

export default function MaxOpenTabsDialog() {
  const pendingTab = useTabStore((s) => s.pendingTab)
  const tabs = useTabStore((s) => s.tabs)
  const cancelPendingTab = useTabStore((s) => s.cancelPendingTab)
  const resolveTabLimit = useTabStore((s) => s.resolveTabLimit)
  const { language } = useLanguageStore()
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const isRTL = language === 'ar'

  const closableTabs = tabs.filter((t) => t.closable !== false)
  const oldest = getOldestClosableTab(tabs)

  if (!pendingTab) return null

  const closeAndOpen = (tabId?: string) => {
    const closingId = tabId ?? oldest?.id
    const closing = tabs.find((t) => t.id === closingId)
    if (closing) {
      destroyKeepAliveForTab(closing.id)
      if (closing.lastPath) clearPersistentStatesForPath(closing.lastPath)
    }
    const opened = resolveTabLimit(closingId)
    if (opened?.lastPath) {
      navigate(opened.lastPath)
    }
  }

  const pendingTitle = pendingTab.title || pendingTab.lastPath

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={cancelPendingTab}
      />
      <div
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        dir={isRTL ? 'rtl' : 'ltr'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white p-5 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold">{translate('tabs.limitTitle')}</h3>
            <p className="text-white/90 text-sm mt-1">
              {translate('tabs.limitMessage')
                .replace('{max}', String(MAX_OPEN_TABS))
                .replace('{title}', pendingTitle)}
            </p>
          </div>
          <button
            type="button"
            onClick={cancelPendingTab}
            className="text-white/90 hover:text-white hover:bg-white/20 rounded-full p-1 shrink-0"
            aria-label={translate('tabs.cancel')}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            {translate('tabs.limitPick')}
          </p>
          <div className="max-h-64 overflow-y-auto custom-scrollbar rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
            {closableTabs.map((tab) => {
              const isOldest = oldest?.id === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => closeAndOpen(tab.id)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-start hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-colors"
                >
                  <span className="flex-1 truncate text-sm text-gray-800 dark:text-gray-100">
                    {tab.title}
                  </span>
                  {isOldest && (
                    <span className="inline-flex items-center gap-1 shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
                      <FiClock size={12} />
                      {translate('tabs.limitOldest')}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div className={`flex gap-3 mt-4 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
            <button
              type="button"
              onClick={cancelPendingTab}
              className="flex-1 px-4 py-2.5 rounded-lg font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {translate('tabs.cancel')}
            </button>
            <button
              type="button"
              onClick={() => closeAndOpen()}
              disabled={!oldest}
              className="flex-[1.4] px-4 py-2.5 rounded-lg font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
            >
              {translate('tabs.limitCloseOldest')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
